import React from 'react';
import { Search } from 'lucide-react';

/**
 * Unified SearchInput component for consistent search bars across tables
 * Minimal, professional design with less colors
 * 
 * @param {string} value - Current search value
 * @param {Function} onChange - Handler for input changes
 * @param {Function} onSearch - Handler when search button is clicked or Enter is pressed (optional for instant search)
 * @param {string} placeholder - Placeholder text for input
 * @param {boolean} disabled - Whether input/search is disabled
 * @param {string} className - Additional CSS classes
 * @param {boolean} showButton - Whether to show search button (default: true)
 * @param {boolean} instantSearch - If true, calls onSearch on every change (default: false)
 */
const SearchInput = ({ 
  value, 
  onChange, 
  onSearch, 
  placeholder = 'Search...',
  disabled = false,
  className = '',
  showButton = true,
  instantSearch = false
}) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (instantSearch && onSearch) {
      onSearch();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed text-sm transition-colors"
      />
      {showButton && (
        <button
          onClick={onSearch}
          disabled={disabled || !onSearch}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded"
          type="button"
          title="Search"
        >
          <Search size={16} className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
